
import { Field, FormSpy, useForm, useFormState } from "react-final-form";
import { CustomButton, RadioField, TitlePage } from "~/components";
import { composeValidators, disabledValidate, required } from "~/utils/validate";
import { Divider, Modal, Select } from "antd";
import { Wizard, FormField } from "~/pages/questionnaires/shared";
import { NamesOfFieldEnum } from "~/typings/types";
import { configTitleFieldsOfCompany } from "../configTitleFields";
import { FullWidthRowFormContainer } from "~/pages/questionnaires/shared/form-field";
import { V2Wizard } from "../../../../shared/wizard";

import styles from "./CommonSteps.module.scss";
import { FloatingLabelSelect } from "~/components/floating-label-select";
import { useScrollAndFocusField, useSharedDataFromBackend } from "~/hooks";
import { useAnalyticsContext } from "~/services/analytics";
import { useCallback, useRef, useState } from "react";
import { postValidatePromocode } from "~/pages/questionnaires/v2/customer/api/apiMethods";
import type { RenderError } from "~/components/error-field";

const { Option } = Select;

interface AdditionalDetailsProps {
  queryPromocode?: string
}

export const AdditionalDetails = ({ queryPromocode }: AdditionalDetailsProps) => {
  useScrollAndFocusField(NamesOfFieldEnum.ClientFrom);

  const analytics = useAnalyticsContext();
  analytics?.trackStep('anketa-customer-company-init_step5');
  analytics?.trackStepWebvisor('webvisor_anketa_customer_step4');

  const { clientFromOrigins } = useSharedDataFromBackend();
  const clientFromOptions = Object.values(clientFromOrigins ?? {}).map(item => ({ label: item.title, value: item.name }));

  const form = useForm();
  const { values: formValues } = useFormState({ subscription: { values: true } });
  const [showOtherPaymentConfirm, setShowOtherPaymentConfirm] = useState(false);

  const [promocodeError, setPromocodeError] = useState<RenderError | null>(null);
  const [isPromocodeChecked, setIsPromocodeChecked] = useState(false);
  const lastCheckedValue = useRef<string | null>(null);

  const handleOtherPaymentClick = useCallback(() => {
    if (formValues?.[NamesOfFieldEnum.PaymentMethodProdamusSubscription] === "other") return;
    setShowOtherPaymentConfirm(true);
  }, [formValues]);

  const confirmOtherPayment = useCallback(() => {
    form.change(NamesOfFieldEnum.PaymentMethodProdamusSubscription, "other");
    setShowOtherPaymentConfirm(false);
  }, [form]);

  const cancelOtherPayment = useCallback(() => {
    setShowOtherPaymentConfirm(false);
  }, []);

  const validatePromocode = useCallback(async (value: string) => {
    if (!value) {
      setPromocodeError(null);
      setIsPromocodeChecked(false);
      lastCheckedValue.current = null;
      return;
    }

    if (lastCheckedValue.current === value) {
      return;
    }
    try {
      const res = await postValidatePromocode({ promo_code: value });
      if (res?.data?.status === "not_found") {
        setPromocodeError({ has: 1, message: "Промокод не найден. Вы можете продолжить без него.", color: "#D97706" });
      } else {
        setPromocodeError({ has: 1, message: "Промокод успешно применен", color: "#437a54" });
      }
    } catch (err: any) {
      const backendMessage = err?.response?.data?.error?.[0]?.message;
      const message = backendMessage ?? "Не удалось проверить промокод, попробуйте позже";
      setPromocodeError({ has: 1, message, color: "#D97706" });
    } finally {
      setIsPromocodeChecked(true);
      lastCheckedValue.current = value;
    }
  }, []);

  const handlePromocodeBlur = useCallback((eventOrValue: React.FocusEvent<HTMLInputElement> | string) => {
    const value = typeof eventOrValue === "string"
      ? eventOrValue.trim()
      : eventOrValue?.target?.value?.trim();

    validatePromocode(value ?? "");
  }, [validatePromocode]);

  return (
    <V2Wizard.Page>
      <TitlePage name="Дополнительные сведения" number="5" />
      <Divider style={{ margin: 0 }} />
      <div className={styles.container}>
        <FullWidthRowFormContainer>
          <FormField
            titleField={
              configTitleFieldsOfCompany[
              NamesOfFieldEnum.PaymentMethodProdamusSubscription
              ]
            }
            name={NamesOfFieldEnum.PaymentMethodProdamusSubscription}
            required
            shouldRenderTitleAboveField
            tooltip="Для ускорения подключения при оплате с расчётного счёта ИП или ЮЛ направьте платежное поручение, так как оплата по счёту может поступить в течение трех дней."
            typeField="radio"
          >
            <div className={styles.paymentMethodWrapper}>
              <Field
                name={NamesOfFieldEnum.PaymentMethodProdamusSubscription}
                type="radio"
                value="settlement_account"
                component={props => <RadioField   {...props} />}
                validate={disabledValidate(composeValidators(required()))}
              >
                Оплачу с расчётного счёта ИП или ЮЛ
              </Field>
              <Field
                name={NamesOfFieldEnum.PaymentMethodProdamusSubscription}
                type="radio"
                value="other"
                validate={disabledValidate(composeValidators(required()))}
              >
                {({ input, meta }) => (
                  <RadioField
                    input={{ ...input, onChange: handleOtherPaymentClick }}
                    meta={meta}
                  >
                    Оплачу иным способом (карта, СБП)
                  </RadioField>
                )}
              </Field>
            </div>
          </FormField>
        </FullWidthRowFormContainer>

        <Modal
          open={showOtherPaymentConfirm}
          onCancel={cancelOtherPayment}
          title="Внимание!"
          centered
          footer={
            <div className={styles.warningModalFooter}>
              <CustomButton
                name="Нет"
                type="button"
                onClick={cancelOtherPayment}
              />
              <CustomButton
                name="Да"
                type="button"
                className={styles.buttonBackWarningModal}
                onClick={confirmOtherPayment}
              />
            </div>
          }
        >
          <p style={{ whiteSpace: 'pre-line', margin: 0 }}>
            Данный метод оплаты увеличивает время проверки.{"\n"}Вы уверены?
          </p>
        </Modal>

        <FullWidthRowFormContainer>
          <FormField
            titleField={configTitleFieldsOfCompany[NamesOfFieldEnum.ClientPromocode]}
            name={NamesOfFieldEnum.ClientPromocode}
            disabled={!!queryPromocode}
            descriptionField={isPromocodeChecked ? undefined : "Если вы подключались без партнёрского промокода, то нажмите «Продолжить»"}
            onBlurCallback={handlePromocodeBlur}
            renderError={promocodeError} />
        </FullWidthRowFormContainer>

        <FullWidthRowFormContainer>
          <FormField
            titleField={configTitleFieldsOfCompany[NamesOfFieldEnum.ClientFrom]}
            name={NamesOfFieldEnum.ClientFrom}
          >
            <Field
              name={NamesOfFieldEnum.ClientFrom}
              subscription={{ submitFailed: true, error: true, submitError: true, value: true }}
              component={FloatingLabelSelect}
              titleField={configTitleFieldsOfCompany[NamesOfFieldEnum.ClientFrom]}
              optionsSelect={clientFromOptions}
            />
          </FormField>
        </FullWidthRowFormContainer>

        <FullWidthRowFormContainer>
          <FormSpy subscription={{ values: true }}>
            {propsSpy => (
              <>
                {propsSpy.values[NamesOfFieldEnum.ClientFrom] === "other" && (
                  <FormField
                    titleField={""}
                    name={NamesOfFieldEnum.ClientFromOther}
                    isColon={false}
                    required
                    validate={composeValidators(required())}
                  />
                )}
              </>
            )}
          </FormSpy>
        </FullWidthRowFormContainer>

      </div>
    </V2Wizard.Page>
  );
};
